/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/**
 * =================================================================================================
 * Quick add
 * =================================================================================================
 *
 * The full-width "Quick add" bar across the bottom of a product card, and the size picker it opens
 * over the lower part of the card photo. Opt-in per render: snippets/product-block.liquid draws the
 * bar when the caller passes quick_add_bar, and emits this JSON block on the card, so opening the
 * picker costs no request:
 *
 *   {"picker": <0-based option index, or -1 when there is nothing to choose>, "pickerName": "Size",
 *    "swatch": <0-based index of the option the card's dots select, or -1>, "swatchName": "Color",
 *    "values": ["S", "M", ...], "defaultSwatch": "Black",
 *    "variants": [[<id>, [<option values>], <available>], ...]}
 *
 * Which picker a card uses is decided by usesSheet(), never by a raw media query: under 768px every
 * card uses this one, and so does a card inside [data-quick-add-sheet="always"] at any width - the
 * product page's suggestion grid and the bag drawer's row, where the theme's inline quick buy panel
 * has nowhere to open. Every other card at 768px and up is left to that panel, untouched.
 *
 * Adding mirrors the theme's own <product-form> request - same FormData, same sections and
 * sections_url, same after-add path, same on:cart:* events - because the theme's quick buy fetches
 * a whole product page into a panel, which is right on a desktop and wrong on a phone.
 */

/* Hidden line-item property marking everything added through quick add, so order exports can tell
   it apart. Underscore-prefixed, so the cart, the checkout and the order page all hide it. */
var QUICK_ADD_PROPERTY_NAME = 'properties[_added_via]';
var QUICK_ADD_PROPERTY_VALUE = 'quick_add';

(function quickAdd() {
  if (!window.matchMedia || !document.addEventListener) return;

  var phone = window.matchMedia('(max-width: 767.98px)');
  var SHEET_ALWAYS = '[data-quick-add-sheet="always"]';
  // The width at which theme.quickBuy (main.js) starts acting on .quickbuy-toggle. Same number,
  // read the same way, so the two cannot disagree on a hairline viewport.
  var THEME_QUICKBUY_MIN_WIDTH = 768;
  // Kept in step with the .quick-add-sheet transition in assets/custom.css.
  var FADE_MS = 200;
  // Scrolling this far from where the picker opened closes it (a finger jitter must not).
  var SCROLL_CLOSE_PX = 24;

  var open = null; // { block, bar, sheet, data, scrollY, swatchValue } while a picker is open
  // With two adds in flight their replies can land out of order; only the reply to the most recent
  // request re-renders the header and drawer, since each reply carries the cart as it stood when
  // Shopify answered it.
  var addSequence = 0;
  var renderedSequence = 0;

  /**
   * The desktop quick buy panel posts the theme's own <product-form>, so its adds are marked by a
   * hidden input added at submit time, in the capture phase, before the theme's handler reads the
   * form. Only a form inside a panel gets it; the product page's own form never does.
   */
  document.addEventListener('submit', function tagQuickBuySubmit(evt) {
    var form = evt.target;
    if (!form || !form.closest || !form.closest('.js-quickbuy')) return;
    if (form.querySelector('input[name="' + QUICK_ADD_PROPERTY_NAME + '"]')) return;
    var marker = document.createElement('input');
    marker.type = 'hidden';
    marker.name = QUICK_ADD_PROPERTY_NAME;
    marker.value = QUICK_ADD_PROPERTY_VALUE;
    form.appendChild(marker);
  }, true);

  /**
   * The one gate for "this card is driven by the in-card picker" - everything asks this rather than
   * testing the width itself, so both cases behave identically.
   * @param {Element} block - The product-block element (may be null).
   * @returns {boolean}
   */
  function usesSheet(block) {
    if (phone.matches) return true;
    return !!(block && block.closest && block.closest(SHEET_ALWAYS));
  }

  /**
   * Reads and caches the card's JSON block. Null when the card carries none or it does not parse,
   * and the bar then stays a plain link to the product page.
   * @param {Element} block - The product-block element.
   * @returns {?object}
   */
  function readData(block) {
    if (block.quickAddData !== undefined) return block.quickAddData;
    var script = block.querySelector('script[data-quick-add-data]');
    var data = null;
    if (script) {
      try {
        data = JSON.parse(script.textContent);
      } catch (error) {
        console.warn('Quick add: could not read card data', error); // eslint-disable-line no-console
      }
    }
    if (data && (!Array.isArray(data.variants) || data.variants.length === 0)) data = null;
    block.quickAddData = data;
    return data;
  }

  /**
   * Whether the card's data is enough to name an exact variant from the card alone. A product with
   * a third option the card does not expose is left to its product page.
   * @param {object} data - Parsed card data.
   * @returns {boolean}
   */
  function quickAddable(data) {
    if (data.variants.length === 1) return true;
    var optionCount = data.variants[0][1].length;
    var exposed = (data.picker > -1 ? 1 : 0) + (data.swatch > -1 ? 1 : 0);
    return optionCount <= exposed;
  }

  /**
   * The card's swatch row for the swatch option, if it rendered one.
   * @param {Element} block
   * @param {object} data
   * @returns {?Element}
   */
  function swatchRow(block, data) {
    if (data.swatch < 0) return null;
    var rows = block.querySelectorAll('.product-block-options[data-option-name]');
    for (var i = 0; i < rows.length; i += 1) {
      if (rows[i].dataset.optionName === data.swatchName) return rows[i];
    }
    return null;
  }

  /**
   * The colour the sizes are for: the photo on show wins, because a swipe changes the colour with no
   * swatch tap; then the tapped swatch; then the colour the card photo showed on load.
   * @param {Element} block
   * @param {object} data
   * @returns {string}
   */
  function currentSwatch(block, data) {
    var row = swatchRow(block, data);
    if (row) {
      var selected = row.querySelector('.product-block-options__item--selected[data-option-item]');
      var active = block.querySelector('.product-block__image--active');
      var activeId = active ? active.dataset.mediaId : '';
      // Two colours can share one photo; when the tapped swatch shows the photo on view it is the
      // shopper's explicit choice and wins over the first swatch carrying that photo.
      if (selected && (!activeId || !selected.dataset.media || selected.dataset.media === activeId)) {
        return selected.dataset.optionItem;
      }
      if (activeId) {
        var items = row.querySelectorAll('.product-block-options__item[data-option-item][data-media]');
        for (var i = 0; i < items.length; i += 1) {
          if (items[i].dataset.media === activeId) return items[i].dataset.optionItem;
        }
      }
      if (selected) return selected.dataset.optionItem;
    }
    return data.defaultSwatch || '';
  }

  /**
   * Puts the selected ring on the swatch the sizes were built for, so the dot and the buttons
   * always agree.
   * @param {Element} block
   * @param {object} data
   * @param {string} value
   */
  function markSwatch(block, data, value) {
    var row = swatchRow(block, data);
    if (!row) return;
    var items = row.querySelectorAll('.product-block-options__item[data-option-item]');
    for (var i = 0; i < items.length; i += 1) {
      items[i].classList.toggle('product-block-options__item--selected', items[i].dataset.optionItem === value);
    }
  }

  /**
   * The one variant matching the picker value and the swatch value, or null.
   * @param {object} data
   * @param {?string} pickerValue - null when the product has no picker option.
   * @param {string} swatchValue
   * @returns {?Array} [id, options, available]
   */
  function variantFor(data, pickerValue, swatchValue) {
    for (var i = 0; i < data.variants.length; i += 1) {
      var options = data.variants[i][1];
      if (data.picker > -1 && options[data.picker] !== pickerValue) continue;
      if (data.swatch > -1 && options[data.swatch] !== swatchValue) continue;
      return data.variants[i];
    }
    return null;
  }

  /**
   * The variant a bar tap adds when there is nothing left to choose: a single-variant product, one
   * whose only option is the one the card's dots select, or one whose picker option has a single
   * value. Null when the sizes have to be shown.
   * @param {Element} block
   * @param {object} data
   * @returns {?Array} [id, options, available]
   */
  function directVariant(block, data) {
    if (data.variants.length === 1) return data.variants[0];
    var swatchValue = currentSwatch(block, data);
    if (data.picker === -1) return variantFor(data, null, swatchValue);
    if (data.values.length === 1) return variantFor(data, data.values[0], swatchValue);
    return null;
  }

  /**
   * Creates the picker once per card, as the last child of the card's image container, so it is
   * positioned over the photo and clipped by it and has no layout effect on anything else.
   * @param {Element} block
   * @returns {Element}
   */
  function ensureSheet(block) {
    var sheet = block.querySelector('.quick-add-sheet');
    if (sheet) return sheet;

    sheet = document.createElement('div');
    sheet.className = 'quick-add-sheet';
    sheet.hidden = true;

    var list = document.createElement('div');
    list.className = 'quick-add-sheet__options';
    list.setAttribute('role', 'group');
    var data = readData(block);
    list.setAttribute('aria-label', (data && data.pickerName) || block.dataset.quickAddAddLabel || '');
    sheet.appendChild(list);

    var error = document.createElement('p');
    error.className = 'quick-add-sheet__error';
    error.setAttribute('role', 'alert');
    error.hidden = true;
    sheet.appendChild(error);

    sheet.addEventListener('click', function onSheetClick(evt) {
      var button = evt.target.closest ? evt.target.closest('.quick-add-sheet__option[data-variant-id]') : null;
      if (!button || !open || open.sheet !== sheet) return;
      evt.preventDefault();
      addVariant(open, button);
    });

    var host = block.querySelector('.image-cont') || block;
    host.appendChild(sheet);
    return sheet;
  }

  /**
   * (Re)builds the buttons for the card's current colour. A sold-out or missing combination renders
   * struck through and disabled, so it cannot be tapped.
   * @param {Element} block
   * @param {object} data
   * @param {Element} sheet
   */
  function renderOptions(block, data, sheet) {
    var list = sheet.querySelector('.quick-add-sheet__options');
    list.innerHTML = '';

    var swatchValue = currentSwatch(block, data);
    if (data.swatch > -1) markSwatch(block, data, swatchValue);
    if (open && open.block === block) open.swatchValue = swatchValue;

    var items = [];
    if (data.variants.length === 1) {
      items.push({ label: block.dataset.quickAddAddLabel || '', variant: data.variants[0] });
    } else if (data.picker > -1) {
      for (var i = 0; i < data.values.length; i += 1) {
        items.push({ label: data.values[i], variant: variantFor(data, data.values[i], swatchValue) });
      }
    } else {
      items.push({ label: swatchValue, variant: variantFor(data, null, swatchValue) });
    }

    var soldOut = block.dataset.quickAddSoldOut || '';
    items.forEach(function build(item) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'quick-add-sheet__option';
      button.textContent = item.label;
      if (item.variant && item.variant[2]) {
        button.dataset.variantId = item.variant[0];
      } else {
        button.classList.add('quick-add-sheet__option--unavailable');
        button.disabled = true;
        if (soldOut) {
          var srOnly = document.createElement('span');
          srOnly.className = 'visually-hidden';
          srOnly.textContent = ' ' + soldOut;
          button.appendChild(srOnly);
        }
      }
      list.appendChild(button);
    });
  }

  function setError(sheet, message) {
    var error = sheet.querySelector('.quick-add-sheet__error');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  /**
   * Opens the picker on a card. Everything it needs is already in the DOM, so this is synchronous:
   * by the time the tap handler returns the buttons exist and the fade has begun.
   * @param {Element} block
   * @param {Element} bar - The tapped quick add bar.
   */
  function openSheet(block, bar) {
    var data = readData(block);
    if (!data) return;
    releaseOpenFor(block);

    var sheet = ensureSheet(block);
    open = {
      block: block, bar: bar, sheet: sheet, data: data, scrollY: window.scrollY, swatchValue: ''
    };
    // renderOptions also rings the swatch the sizes are for.
    renderOptions(block, data, sheet);
    setError(sheet, '');
    sheet.style.bottom = bar.offsetHeight + 'px';
    sheet.hidden = false;
    // One synchronous style read, so the un-hidden state is laid out before is-open lands; without
    // it the browser coalesces both and skips the fade.
    void sheet.offsetWidth; // eslint-disable-line no-void
    sheet.classList.add('is-open');
    block.classList.add('product-block--quick-add-open');
    bar.setAttribute('aria-expanded', 'true');

    bindWhileOpen();
  }

  /**
   * Closes the open picker, fading unless asked to be instant. The element stays in the card for
   * the next open.
   * @param {boolean} [instant]
   */
  function closeSheet(instant) {
    if (!open) return;
    var state = open;
    open = null;
    unbindWhileOpen();
    hideSheet(state, instant);
  }

  /**
   * Fades a card's picker out without touching the `open` singleton: used by closeSheet, and by a
   * finished add on a card whose picker was left showing its spinner while another card was opened.
   * @param {object} state - { block, bar, sheet }
   * @param {boolean} [instant]
   */
  function hideSheet(state, instant) {
    state.sheet.classList.remove('is-open');
    state.sheet.classList.remove('is-adding');
    state.block.classList.remove('product-block--quick-add-open');
    state.bar.setAttribute('aria-expanded', 'false');

    var finish = function finishClose() {
      if (!state.sheet.classList.contains('is-open')) state.sheet.hidden = true;
    };
    if (instant) finish(); else setTimeout(finish, FADE_MS);
  }

  /**
   * Makes way for another card: a picker that is not mid-request closes at once; one whose card has
   * a request in flight is left showing its spinner - the request settles it - and only its
   * tracking and its listeners are dropped.
   * @param {Element} block
   */
  function releaseOpenFor(block) {
    if (!open || open.block === block) return;
    if (open.block.quickAddBusy) {
      open = null;
      unbindWhileOpen();
    } else {
      closeSheet(true);
    }
  }

  function onScroll() {
    if (!open) return;
    if (open.block.quickAddBusy) return; // a card mid-request keeps its picker and its spinner
    if (Math.abs(window.scrollY - open.scrollY) > SCROLL_CLOSE_PX) closeSheet();
  }

  function onKeydown(evt) {
    if (!open || evt.key !== 'Escape') return;
    var bar = open.bar;
    closeSheet();
    if (bar && typeof bar.focus === 'function') bar.focus();
  }

  function onBreakpointChange(evt) {
    // Growing past the phone breakpoint hands a normal card back to the theme's desktop panel, so
    // its picker must go. An "always" card has no panel to hand back to, so its picker stays.
    if (!evt.matches && open && !usesSheet(open.block)) closeSheet(true);
  }

  // A swipe across the open card's photos changes the colour on show without any tap; the theme
  // marks the new photo with a class, so watch for that and rebuild the sizes for it.
  var imageObserver = window.MutationObserver ? new MutationObserver(function onImageChange(records) {
    if (!open) return;
    for (var i = 0; i < records.length; i += 1) {
      var target = records[i].target;
      if (target.classList && target.classList.contains('product-block__image')) {
        if (!open.block.quickAddBusy && currentSwatch(open.block, open.data) !== open.swatchValue) {
          setError(open.sheet, '');
          renderOptions(open.block, open.data, open.sheet);
        }
        return;
      }
    }
  }) : null;

  function bindWhileOpen() {
    if (imageObserver) imageObserver.observe(open.block, { attributes: true, attributeFilter: ['class'], subtree: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('keydown', onKeydown);
    if (phone.addEventListener) phone.addEventListener('change', onBreakpointChange);
    else if (phone.addListener) phone.addListener(onBreakpointChange);
  }

  function unbindWhileOpen() {
    if (imageObserver) imageObserver.disconnect();
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('keydown', onKeydown);
    if (phone.removeEventListener) phone.removeEventListener('change', onBreakpointChange);
    else if (phone.removeListener) phone.removeListener(onBreakpointChange);
  }

  /**
   * Runs `fn` once the cart DOM has been replaced: on the theme's on:cart:after-merge, or after a
   * short fallback wait in case that event never comes, so the work is never dropped.
   * @param {Function} fn
   */
  function afterNextMerge(fn) {
    var done = false;
    var run = function () {
      if (done) return;
      done = true;
      document.removeEventListener('on:cart:after-merge', run);
      fn();
    };
    document.addEventListener('on:cart:after-merge', run);
    setTimeout(run, 3000);
  }

  /**
   * Puts focus back inside the bag drawer after an add made from a card inside it: the re-render
   * replaces the card and the button the shopper was on, dropping focus to <body> outside an
   * aria-modal drawer. The theme's own trapFocus() is reused because it re-scans the drawer (its
   * captured list now holds detached nodes) and, with no element to focus, lands on the drawer
   * itself - where SideDrawer.open() would have put it.
   * @param {Element} drawer - The cart drawer, still open.
   */
  function restoreDrawerFocus(drawer) {
    if (!drawer.hasAttribute('open')) return;
    var active = document.activeElement;
    // Focus inside a card being faded out, or inside a picker already hidden, is about to be
    // dropped to <body> by their removal, so it counts as lost rather than as the shopper's own.
    var held = active && active !== document.body && drawer.contains(active)
      && !(active.closest && (active.closest('.merge-remove-wrapper') || active.closest('.merge-remove-item') || active.closest('[hidden]')));
    if (held) return;
    focusDrawerWithoutScrolling(drawer);
  }

  /**
   * A line put in at the top of the bag while the drawer is scrolled down pushes everything
   * under the shopper's finger down by its height, which reads as the drawer jumping. The scroll
   * position is moved by the same amount in the same frame, so what is on screen stays where it
   * is; the new line waits above, where the shopper scrolls up to it. Nothing to do when the
   * drawer is at the top: the line then simply appears in view.
   * @param {Element} drawer
   * @param {Element} line - The line just inserted at the top of the bag.
   */
  function keepDrawerViewStill(drawer, line) {
    var content = drawer.querySelector('.cart-drawer__content');
    if (!content || content.scrollTop <= 0) return;
    content.scrollTop += line.getBoundingClientRect().height;
  }

  /**
   * Focuses the drawer (through the theme's own trapFocus, which re-scans it) while keeping its
   * content exactly where the shopper scrolled it: focusing an element scrolls it into view, which
   * showed as a small jump upward after every add made from inside the drawer.
   * @param {Element} drawer
   */
  function focusDrawerWithoutScrolling(drawer) {
    var content = drawer.querySelector('.cart-drawer__content');
    var top = content ? content.scrollTop : 0;
    if (typeof window.trapFocus === 'function') window.trapFocus(drawer);
    else if (typeof drawer.focus === 'function') drawer.focus({ preventScroll: true });
    if (content) content.scrollTop = top;
  }

  /* ---------------------------------------------------------------------------------------------
   * Showing the add before Shopify has answered it
   *
   * The drawer opens on the tap and is given a bag line built from what the card already knows -
   * photo, title, the tapped variant's option values, the card's price - so the shopper sees the
   * add at once rather than after a round trip. The reply then replaces that line's contents in
   * place: the provisional line is handed the key the reply gave the real one, so the theme's
   * merge treats the two as the same line instead of fading one out and another in.
   * ------------------------------------------------------------------------------------------ */

  /* The key a provisional line carries until the reply names it. No reply key can look like this,
     so a line the reply never names is still settled: the merge fades it out and reveals the real
     one, which is the theme's own behaviour. */
  var PROVISIONAL_KEY_PREFIX = 'quick-add-provisional:';

  /* Every quick add is for one item. Named once, so the request and the quantity the provisional
     line states can never disagree. */
  var QUICK_ADD_QUANTITY = 1;

  /* How many adds are waiting on Shopify. A count rather than a flag: two cards can be tapped in
     quick succession, and the first to settle must not release a state the second still needs. */
  var pendingAdds = 0;

  /* The provisional lines still waiting on a reply. A reply describes the cart without the adds
     that are still in flight, so the merge's remove pass would fade their lines out; they are
     lifted out of the list for the merge and put straight back. */
  var pendingLines = [];

  /* Set when the shopper closes the drawer while an add is in flight, and held until every add
     and every re-render one of them started has settled. The theme asks for a closed drawer to be
     re-opened at the end of each merge, so one add's merge must not spend a suppression a later
     add - or a rejected add's own asynchronous refresh - still needs. */
  var drawerClosedWhilePending = false;

  /* Re-renders the theme will do on its own, some time after the add that asked for them. */
  var pendingRefreshes = 0;

  /* The provisional line whose own reply is being merged right now: it stays in the list so the
     merge can rewrite it in place. Null for every merge this file did not start. */
  var mergingLine = null;

  /* The bag lines this session's quick adds produced, by their stable identity - the key without
     the price and the discounts, which change under it. Only a line in here is treated as one a
     repeat quick add would add to; a line the product page put in the bag carries different
     properties, so Shopify gives that add its own line and so does this file. */
  var adoptedLines = [];

  /* The drawer's empty state, as the section writes it: three merged classes plus the checkout
     button's own. All four are switched together, so the first item in an empty bag shows the
     content, the bar and the checkout button at once. */
  var DRAWER_EMPTY_PARTS = [
    { selector: '[data-merge-attributes="content-container"]', className: 'cart-drawer__content--hidden', whenEmpty: true },
    { selector: '[data-merge-attributes="footer-container"]', className: 'cart-drawer__footer--hidden', whenEmpty: true },
    { selector: '[data-merge-attributes="checkout-buttons"]', className: 'hidden', whenEmpty: true },
    { selector: '[data-merge-attributes="empty-container"]', className: 'cart-drawer__empty-content--hidden', whenEmpty: false }
  ];

  function make(tag, className) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    return element;
  }

  /**
   * The one variant row in the card's data with this id.
   * @param {object} data - Parsed card data.
   * @param {string|number} id
   * @returns {?Array} [id, options, available]
   */
  function variantById(data, id) {
    var wanted = String(id);
    for (var i = 0; i < data.variants.length; i += 1) {
      if (String(data.variants[i][0]) === wanted) return data.variants[i];
    }
    return null;
  }

  /**
   * The option values the bag line names, in the product's own option order. A single-variant
   * product names none, as snippets/cart-item.liquid does not either.
   * @param {object} data - Parsed card data.
   * @param {Array} variant - [id, optionValues, available].
   * @returns {Array} [{ name, value }, ...]
   */
  function chosenOptions(data, variant) {
    if (data.variants.length < 2) return [];
    var values = variant[1] || [];
    var chosen = [];
    if (data.swatch > -1) chosen.push({ index: data.swatch, name: data.swatchName, value: values[data.swatch] });
    if (data.picker > -1) chosen.push({ index: data.picker, name: data.pickerName, value: values[data.picker] });
    // A variant row the card did not describe leaves no value here. No line at all is better
    // than a line reading the name of nothing.
    return chosen
      .filter(function named(option) { return typeof option.value === 'string' && option.value !== ''; })
      .sort(function byOptionOrder(a, b) { return a.index - b.index; });
  }

  /* The widths snippets/cart-item.liquid renders a bag line's photo at. Warmed while the add is
     in flight, so the confirmed line paints from cache instead of blanking. */
  var CART_LINE_IMAGE_WIDTHS = [200, 100];

  /** Starts the browser fetching each URL, with nothing waiting on the result. */
  function warmUrls(urls) {
    urls.forEach(function warm(url) {
      var probe = new Image();
      probe.src = url;
    });
  }

  /** The candidate URLs in a srcset, without their width descriptors. */
  function srcsetUrls(srcset) {
    if (!srcset) return [];
    return srcset.split(',').map(function candidate(entry) {
      return entry.trim().split(/\s+/)[0];
    }).filter(function present(url) { return url; });
  }

  /**
   * The URLs a bag line will ask for, worked out from a card photo's URL: the same file and
   * version at the bag line's own widths, with the card's cropping dropped, because the bag line
   * renders its photo uncropped.
   * @param {string} src - The card photo's URL.
   * @returns {Array<string>}
   */
  function cartLineImageUrls(src) {
    if (!src) return [];
    try {
      var url = new URL(src, window.location.href);
      ['crop', 'crop_left', 'crop_top', 'crop_width', 'crop_height', 'height'].forEach(function drop(name) {
        url.searchParams.delete(name);
      });
      return CART_LINE_IMAGE_WIDTHS.map(function at(width) {
        url.searchParams.set('width', width);
        return url.href;
      });
    } catch (error) {
      return [];
    }
  }

  /**
   * The figure inside one of the card's price spans. The theme puts the money in the span's last
   * child, after any "from" label.
   * @param {?Element} priceEl
   * @returns {string}
   */
  function moneyText(priceEl) {
    if (!priceEl) return '';
    var last = priceEl.lastElementChild;
    return (last ? last.textContent : priceEl.textContent).trim();
  }

  /**
   * The price column, in the bag line's own classes rather than the card's. A card shows its
   * cheapest variant's price, so this is an estimate for as long as the line is provisional; the
   * reply replaces it with the line's real price.
   * @param {Element} block
   * @returns {Element}
   */
  function provisionalPrice(block) {
    var column = make('div', 'cart-item__column cart-item__price');
    var source = block.querySelector('.product-price');
    var selling = make('span', 'theme-money cart-item__selling-price');
    selling.textContent = moneyText(source ? source.querySelector('.price__current') : null);
    column.appendChild(selling);

    var wasText = moneyText(source ? source.querySelector('.price__was') : null);
    if (wasText) {
      var struck = make('span', 'struck-out-price small-text');
      var money = make('span', 'theme-money');
      money.textContent = wasText;
      struck.appendChild(money);
      column.appendChild(struck);
    }
    return column;
  }

  /**
   * Builds the bag line the drawer shows while the add is in flight: the same classes and columns
   * snippets/cart-item.liquid renders for a drawer line, without the quantity control - whose
   * buttons would act on a line the bag does not have yet - and with no data-merge-cache, because
   * the theme's merge only rewrites a matched line whose cache differs from the reply's.
   * @param {Element} block - The card the add was made from.
   * @param {object} data - The card's parsed JSON block.
   * @param {Array} variant - [id, optionValues, available] for the variant being added.
   * @returns {Element} The [data-merge-list-item] wrapper.
   */
  function buildProvisionalLine(block, data, variant) {
    var link = block.querySelector('a.product-link');
    var href = link ? link.getAttribute('href') : '';

    var wrapper = make('div');
    wrapper.dataset.mergeListItem = PROVISIONAL_KEY_PREFIX + variant[0];
    var item = make('div', 'cart-item cart-item--adding');
    wrapper.appendChild(item);

    var imageColumn = make('div', 'cart-item__column cart-item__image');
    var imageLink = make('a', 'cart-item__image-link');
    if (href) imageLink.href = href;
    // The photo the card is showing, at the URL the browser already has in cache, so the line
    // paints in the same frame it is inserted. Its intrinsic size comes with it, so the row
    // reserves the right height before the reply lands.
    var source = block.querySelector('.product-block__image--active img');
    var image = make('img', 'theme-img');
    var src = source ? (source.currentSrc || source.getAttribute('src') || source.dataset.manualSrc) : '';
    if (src) image.src = src;
    image.alt = '';
    if (source && source.naturalWidth) {
      image.width = source.naturalWidth;
      image.height = source.naturalHeight;
    }
    imageLink.appendChild(image);
    imageColumn.appendChild(imageLink);
    item.appendChild(imageColumn);

    var notImage = make('div', 'cart-item__not-image');
    item.appendChild(notImage);

    var description = make('div', 'cart-item__column cart-item__description');
    var row = make('div', 'lightly-spaced-row');
    var titleBox = make('div', 'cart-item__title');
    var titleLink = make('a', 'text-current');
    if (href) titleLink.href = href;
    var title = block.querySelector('.product-block__title');
    titleLink.textContent = title ? title.textContent.trim() : '';
    titleBox.appendChild(titleLink);
    row.appendChild(titleBox);

    var small = make('div', 'small-text');
    chosenOptions(data, variant).forEach(function addOption(option) {
      var line = make('div', 'cart-item__variant');
      var label = make('span', 'cart-item__variant-label');
      label.textContent = option.name + ':';
      var value = make('span', 'cart-item__variant-value');
      value.textContent = option.value;
      line.appendChild(label);
      line.appendChild(document.createTextNode(' '));
      line.appendChild(value);
      small.appendChild(line);
    });
    row.appendChild(small);
    description.appendChild(row);
    notImage.appendChild(description);

    notImage.appendChild(provisionalPrice(block));

    // The quantity column states the quantity the add is for and keeps the footprint of the
    // control the confirmed line brings, so the swap does not push the rest of the bag down. It
    // carries none of that control's hooks, so nothing in it can act on a line the bag has not
    // got yet.
    var quantity = make('div', 'cart-item__column cart-item__quantity');
    var quantityBox = make('div', 'buttoned-input buttoned-input--small cart-item__quantity-placeholder');
    var quantityValue = make('span');
    quantityValue.textContent = QUICK_ADD_QUANTITY;
    quantityBox.appendChild(quantityValue);
    quantity.appendChild(quantityBox);
    notImage.appendChild(quantity);

    return wrapper;
  }

  /**
   * Switches the drawer between its empty and its filled state.
   * @param {Element} drawer
   * @param {boolean} empty
   */
  function setDrawerEmptyState(drawer, empty) {
    DRAWER_EMPTY_PARTS.forEach(function apply(part) {
      var node = drawer.querySelector(part.selector);
      if (node) node.classList.toggle(part.className, part.whenEmpty ? empty : !empty);
    });
  }

  /**
   * Holds checkout closed while an add is in flight. A provisional line is not a line Shopify has
   * agreed to: from an empty bag the checkout link is shown the moment the size is tapped, and
   * without this a shopper could be at checkout with nothing in the cart.
   * @param {Element} drawer
   * @param {boolean} pending
   */
  function setCheckoutPending(drawer, pending) {
    drawer.querySelectorAll('[data-merge-attributes="checkout-buttons"] .btn').forEach(function apply(button) {
      button.classList.toggle('checkout-buttons__btn--pending', pending);
      if (pending) {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('disabled', '');
        button.setAttribute('tabindex', '-1');
      } else {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        button.removeAttribute('disabled');
        button.removeAttribute('tabindex');
      }
    });
  }

  /**
   * Records an add as waiting on Shopify. Checkout closes on the first one and only reopens when
   * the last has settled, either way.
   * @param {Element} drawer
   */
  function beginPendingAdd(drawer) {
    pendingAdds += 1;
    if (pendingAdds === 1) setCheckoutPending(drawer, true);
  }

  /**
   * Records a waiting add as settled - confirmed or failed, both release it.
   * @param {Element} drawer
   */
  function endPendingAdd(drawer) {
    pendingAdds = Math.max(0, pendingAdds - 1);
    if (pendingAdds > 0) return;
    setCheckoutPending(drawer, false);
    releaseDrawerCloseSuppression();
  }

  /** Lets the theme re-open the drawer again, once nothing this file started is still running. */
  function releaseDrawerCloseSuppression() {
    if (pendingAdds > 0 || pendingRefreshes > 0) return;
    drawerClosedWhilePending = false;
  }

  /**
   * Counts a re-render the theme will do on its own. afterNextMerge is what ends it, so a
   * re-render that never lands cannot hold anything open for ever.
   */
  function beginPendingRefresh() {
    pendingRefreshes += 1;
    afterNextMerge(function refreshSettled() {
      pendingRefreshes = Math.max(0, pendingRefreshes - 1);
      releaseDrawerCloseSuppression();
    });
  }

  // A close the shopper made while an add was in flight stands. The theme's CartForm asks for a
  // closed drawer to be re-opened once a merge finishes (a theme:open-cart-drawer event), which
  // made sense while the drawer was only ever opened after the reply; now that it opens on the
  // tap, that request would undo the shopper's own action, so it is turned down once.
  document.addEventListener('on:cart-drawer:before-close', function onDrawerClose() {
    if (pendingAdds > 0) drawerClosedWhilePending = true;
  });

  document.addEventListener('theme:open-cart-drawer', function onThemeReopen(evt) {
    if (!drawerClosedWhilePending) return;
    evt.stopImmediatePropagation();
  }, true);

  // The attributes above state the closed door; this is what holds it. An anchor ignores the
  // disabled attribute, and a stylesheet that failed to load would leave the link live.
  document.addEventListener('click', function onCheckoutTap(evt) {
    if (pendingAdds < 1 || !evt.target.closest) return;
    if (!evt.target.closest('[data-merge-attributes="checkout-buttons"] .btn')) return;
    evt.preventDefault();
    evt.stopPropagation();
  }, true);

  /**
   * A line's identity, without the price and the discounts a key also carries: a key is
   * `item-key:<variant id>:<properties hash>|<price>|<discounts…>`, and only the part before the
   * first bar names the same line from one render to the next.
   * @param {string} key
   * @returns {string}
   */
  function lineIdentity(key) {
    return (key || '').split('|')[0];
  }

  /** Remembers a line as one a repeat quick add would add to. */
  function rememberAdoptedLine(key) {
    var identity = lineIdentity(key);
    if (identity && adoptedLines.indexOf(identity) === -1) adoptedLines.push(identity);
  }

  /**
   * The bag line a repeat quick add of this variant would add to, if there is one.
   *
   * A variant on its own is not enough to name a line: quick add sends a hidden property the
   * product page's own form does not, so the same variant added from each sits on two separate
   * lines with two different keys. Only a line this session's quick adds produced is treated as
   * ours; anything else gets its own provisional line, and adoptReplyKey settles it against what
   * Shopify actually did.
   * @param {Element} list - The [data-merge-list="cart-items"] element.
   * @param {string|number} variantId
   * @returns {?Element}
   */
  function lineForVariant(list, variantId) {
    var prefix = 'item-key:' + variantId + ':';
    var items = list.querySelectorAll('[data-merge-list-item]');
    for (var i = 0; i < items.length; i += 1) {
      var identity = lineIdentity(items[i].dataset.mergeListItem);
      if (identity.indexOf(prefix) === 0 && adoptedLines.indexOf(identity) > -1) return items[i];
    }
    return null;
  }

  /**
   * Opens the drawer and shows the add, in the tap's own frame. Returns the state the reply needs
   * to settle it, or null where the merchant's after-add setting does not lead to the drawer.
   * @param {Element} block - The card the add was made from.
   * @param {object} data - The card's parsed JSON block.
   * @param {Array} variant - [id, optionValues, available].
   * @param {Element} opener - The element that was tapped.
   * @returns {?object}
   */
  function startInstantAdd(block, data, variant, opener) {
    if (document.querySelector('.template-cart')) return null;
    if (theme.settings.afterAddToCart !== 'drawer') return null;
    var drawer = document.querySelector('.js-cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return null;

    var state = { drawer: drawer, line: null, existing: null, wasEmpty: false, opened: false, deferOpen: false, opener: opener };
    var list = drawer.querySelector('[data-merge-list="cart-items"]');
    // An empty bag has nothing to show around the new line yet (no bar progress, no
    // suggestions), so the drawer waits for the reply and opens complete; a bag with lines
    // opens on the tap, with the new line already in it.
    var emptyNow = !list || !list.querySelector('[data-merge-list-item]');
    if (!drawer.hasAttribute('open')) {
      if (emptyNow) {
        state.deferOpen = true;
      } else {
        drawer.open(opener);
        state.opened = true;
      }
    }
    if (!list) return state;

    // A variant the bag already holds keeps its line - the reply restates its quantity - so only
    // the waiting state is shown on it.
    var existing = lineForVariant(list, variant[0]);
    if (existing) {
      state.existing = existing;
      var existingItem = existing.querySelector('.cart-item');
      if (existingItem) existingItem.classList.add('cart-item--adding');
      return state;
    }

    state.wasEmpty = !list.querySelector('[data-merge-list-item]');
    if (state.wasEmpty) setDrawerEmptyState(drawer, false);
    state.line = buildProvisionalLine(block, data, variant);

    // Put in at full height with a short fade of its own, not through the theme's insert
    // animation: that one opens the row's height while the line is still transparent and only
    // fades it in afterwards, which would leave it invisible for longer than the reply often
    // takes - the opposite of what showing the add at once means.
    state.line.classList.add('cart-item-line--entering');
    list.insertBefore(state.line, list.firstChild);
    // One synchronous style read, so the entering state is laid out before it is taken away;
    // without it the browser coalesces both and the line appears rather than fades.
    void state.line.offsetWidth; // eslint-disable-line no-void
    state.line.classList.remove('cart-item-line--entering');
    pendingLines.push(state.line);
    keepDrawerViewStill(drawer, state.line);

    // The bag line renders its photo from a different, much smaller URL than the card's. Asking
    // for it now means the confirmed line paints from cache instead of blanking while it loads.
    var source = block.querySelector('.product-block__image--active img');
    warmUrls(cartLineImageUrls(source ? (source.currentSrc || source.getAttribute('src') || source.dataset.manualSrc) : ''));

    return state;
  }

  /** Stops tracking a provisional line, whichever way its add ended. */
  function releaseProvisional(state) {
    if (!state || !state.line) return;
    var at = pendingLines.indexOf(state.line);
    if (at > -1) pendingLines.splice(at, 1);
  }

  /**
   * Lifts the provisional lines out of a list that is about to be merged. Their keys are ones no
   * reply can name, so the merge's remove pass would fade them out under the shopper; out of the
   * list, it never sees them. The line whose own reply is being merged stays, so the merge can
   * rewrite it in place.
   * @param {Element} container - The element being merged into.
   * @returns {Array} The lifted lines, for reattachProvisionals.
   */
  function detachPendingProvisionals(container) {
    var lifted = [];
    pendingLines.forEach(function lift(line) {
      if (line === mergingLine || !line.parentNode) return;
      if (container && !container.contains(line)) return;
      lifted.push({ line: line, list: line.parentNode });
      line.parentNode.removeChild(line);
    });
    return lifted;
  }

  /**
   * Puts the lifted lines back where they were, without any animation: the shopper has been
   * looking at them all along, so they must not fade in a second time.
   * @param {Array} lifted - From detachPendingProvisionals.
   */
  function reattachProvisionals(lifted) {
    lifted.forEach(function put(entry) {
      entry.line.classList.remove('cart-item-line--entering');
      entry.list.insertBefore(entry.line, entry.list.firstChild);
    });
  }

  // Every drawer re-render goes through the theme's merge, whoever started it: this add's own
  // reply, the theme's asynchronous fallback, a quantity change, the product page's own form. The
  // protection sits here rather than around the one call this file makes, so a merge nobody told
  // us about cannot take a provisional line with it. The wrapper finishes before the theme
  // announces the merge, so everything listening for that event sees the list whole.
  if (window.theme && typeof window.theme.mergeNodes === 'function') {
    var themeMergeNodes = window.theme.mergeNodes;
    window.theme.mergeNodes = function mergeNodes(newContent, targetContainer) {
      var lifted = detachPendingProvisionals(targetContainer);
      try {
        themeMergeNodes(newContent, targetContainer);
      } finally {
        reattachProvisionals(lifted);
      }
    };
  }

  /**
   * Warms the photo the confirmed line will ask for when it is a different file from the card's -
   * a variant carrying its own image. Read from the reply before the merge, so the request is
   * already in flight when the line's <img> asks for the same URL.
   * @param {?object} state - From startInstantAdd.
   * @param {DocumentFragment} reply - The parsed drawer section from the reply.
   */
  function warmReplyLineImage(state, reply) {
    if (!state || !state.line) return;
    var key = state.line.dataset.mergeListItem;
    var candidates = reply.querySelectorAll('[data-merge-list-item]');
    for (var i = 0; i < candidates.length; i += 1) {
      if (candidates[i].dataset.mergeListItem !== key) continue;
      var image = candidates[i].querySelector('.cart-item__image img');
      if (!image) return;
      // The srcset candidates only: with sizes="100px" the browser picks one of those, never the
      // full-width src the theme also emits.
      warmUrls(srcsetUrls(image.getAttribute('data-manual-srcset') || image.getAttribute('srcset')));
      return;
    }
  }

  /**
   * Hands the provisional line the key the reply gave the real one, so the merge rewrites it in
   * place. Only the key is copied: the merge rewrites a matched line when the caches differ, and
   * a line with no cache at all always differs.
   * @param {?object} state - From startInstantAdd.
   * @param {DocumentFragment} reply - The parsed drawer section from the reply.
   */
  function adoptReplyKey(state, reply) {
    if (!state || !state.line) return;
    var list = state.drawer.querySelector('[data-merge-list="cart-items"]');
    if (!list) return;

    var taken = [];
    list.querySelectorAll('[data-merge-list-item]').forEach(function note(node) {
      if (node !== state.line) taken.push(node.dataset.mergeListItem);
    });

    var prefix = 'item-key:' + state.variantId + ':';
    var candidates = reply.querySelectorAll('[data-merge-list-item]');
    var namedButPresent = null;
    for (var i = 0; i < candidates.length; i += 1) {
      var key = candidates[i].dataset.mergeListItem;
      if (key.indexOf(prefix) !== 0) continue;
      if (taken.indexOf(key) === -1) {
        state.line.dataset.mergeListItem = key;
        rememberAdoptedLine(key);
        return;
      }
      namedButPresent = key;
    }

    // The reply names no new line for this variant but does name one the drawer already shows:
    // Shopify added to a line the bag had rather than making another. The provisional line stands
    // for nothing, so it goes now - before the merge, so nothing has to fade it out - and the
    // waiting state moves to the line that really changed.
    if (!namedButPresent) return;
    takeBackProvisionalLine(state);
    rememberAdoptedLine(namedButPresent);
    for (var j = 0; j < list.children.length; j += 1) {
      if (list.children[j].dataset.mergeListItem !== namedButPresent) continue;
      state.existing = list.children[j];
      var existingItem = state.existing.querySelector('.cart-item');
      if (existingItem) existingItem.classList.add('cart-item--adding');
      return;
    }
  }

  /**
   * Takes the provisional line straight out of the list, with no animation and without disturbing
   * the empty state: used where it has been superseded rather than refused.
   * @param {object} state - From startInstantAdd.
   */
  function takeBackProvisionalLine(state) {
    if (!state.line) return;
    releaseProvisional(state);
    if (state.line.parentNode) state.line.parentNode.removeChild(state.line);
    state.line = null;
  }

  /**
   * Settles a provisional line against the list a merge has just drawn, for the path where the
   * reply carried no sections to read from: the merge has put the real line in beside ours, so
   * ours takes over its key and its contents and the duplicate goes. A merge that named no line
   * for this variant leaves the provisional line alone - it stands for an add Shopify accepted,
   * and the next merge will settle it.
   * @param {?object} state - From startInstantAdd.
   */
  function adoptMergedLine(state) {
    if (!state || !state.line || !state.line.isConnected) return;
    if (state.line.dataset.mergeListItem.indexOf(PROVISIONAL_KEY_PREFIX) !== 0) return;
    var list = state.drawer.querySelector('[data-merge-list="cart-items"]');
    if (!list) return;

    var prefix = 'item-key:' + state.variantId + ':';
    var items = list.querySelectorAll('[data-merge-list-item]:not(.merge-remove-item)');
    for (var i = 0; i < items.length; i += 1) {
      var real = items[i];
      if (real === state.line || real.dataset.mergeListItem.indexOf(prefix) !== 0) continue;
      state.line.dataset.mergeListItem = real.dataset.mergeListItem;
      if (real.dataset.mergeCache) state.line.dataset.mergeCache = real.dataset.mergeCache;
      state.line.innerHTML = real.innerHTML;
      rememberAdoptedLine(real.dataset.mergeListItem);
      real.remove();
      return;
    }
  }

  /**
   * Drops the waiting state once the reply has landed. The line is at its own full height
   * throughout, so there is nothing to re-measure.
   * @param {?object} state - From startInstantAdd.
   */
  function settleInstantAdd(state) {
    if (!state) return;
    releaseProvisional(state);
    [state.line, state.existing].forEach(function clear(node) {
      if (!node) return;
      node.querySelectorAll('.cart-item--adding').forEach(function unmark(item) {
        item.classList.remove('cart-item--adding');
      });
    });
  }

  /**
   * Takes the add back off the drawer after a failed request: the provisional line fades out and
   * an emptied bag goes back to its empty state.
   * @param {?object} state - From startInstantAdd.
   */
  function abandonInstantAdd(state) {
    if (!state) return;
    if (state.existing) {
      var existingItem = state.existing.querySelector('.cart-item');
      if (existingItem) existingItem.classList.remove('cart-item--adding');
    }
    if (state.line) {
      releaseProvisional(state);
      theme.hideAndRemove(state.line);
    }

    // Both questions below are asked of the bag as it stands, never of what it held when this add
    // started: another add may have filled it since, and showing "your cart is empty" over a real
    // line is worse than the failure itself. This add is still counted in pendingAdds here, so
    // one pending add means there are none besides it.
    var list = state.drawer.querySelector('[data-merge-list="cart-items"]');
    var linesLeft = list ? list.querySelector('[data-merge-list-item]:not(.merge-remove-item)') : null;
    if (!list || linesLeft || pendingAdds > 1) return;

    setDrawerEmptyState(state.drawer, true);
    // A drawer this add opened is put away again, so the card's own error is not left behind it.
    if (state.opened && typeof state.drawer.close === 'function') state.drawer.close();
  }

  /**
   * Adds the tapped button's exact variant, mirroring the theme's <product-form> request and its
   * after-add path so the drawer, the header count and every listener behave as on the product
   * page.
   * @param {object} state - The open picker's state.
   * @param {Element} button - The tapped size button, or the bar itself for a direct add.
   * @param {string|number} [directId] - Variant id for a direct add, where the bar carries none.
   */
  async function addVariant(state, button, directId) {
    var block = state.block;
    var bar = state.bar;
    var sheet = state.sheet;
    // One request at a time per card, whatever happens to its picker meanwhile: a second tap on
    // the same card is ignored until it settles, while a tap on another card is not held up.
    if (block.quickAddBusy || sheet.classList.contains('is-adding')) return;

    block.quickAddBusy = true;
    sheet.classList.add('is-adding');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    setError(sheet, '');

    var variantId = directId || button.dataset.variantId;
    // Before anything that waits: the drawer opens and shows the line, from what the card knows.
    var variant = variantById(state.data, variantId) || [variantId, [], true];
    var instant = startInstantAdd(block, state.data, variant, bar);
    if (instant) {
      instant.variantId = variantId;
      beginPendingAdd(instant.drawer);
    }

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', QUICK_ADD_QUANTITY);
    formData.append(QUICK_ADD_PROPERTY_NAME, QUICK_ADD_PROPERTY_VALUE);

    var sectionsToUpdate = ['page-header', 'cart-drawer']
      .map(function find(sel) { return document.querySelector(sel); })
      .filter(function present(el) { return el; });
    formData.append('sections_url', window.location.pathname);
    formData.append('sections', sectionsToUpdate.map(function id(el) { return el.dataset.sectionId; }).join(','));

    var responseText = '';
    addSequence += 1;
    var sequence = addSequence;

    // An add from a card inside the drawer re-renders the drawer, and the merge replaces the whole
    // cross-sells region - so the card is detached by the time the events below fire, and an event
    // bubbling from a detached node reaches nothing. Dispatch from the card while it is in the page
    // and from `document` once it is not, so the theme's own listeners still hear it.
    var dispatchOn = function dispatchOn() { return block.isConnected ? block : document; };

    // Whether the shopper tapped inside the open bag drawer, read before the request while the card
    // is still in the page.
    var addedFromDrawer = !!(bar && bar.closest && bar.closest('.js-cart-drawer'));
    var mergeIsAsync = false;

    try {
      var response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      var data = await response.json();

      if (typeof data.description === 'string') responseText = data.description;
      else if (typeof data.message === 'string') responseText = data.message;
      if (data.errors && typeof data.errors === 'object') {
        responseText = Object.keys(data.errors).map(function join(key) { return [].concat(data.errors[key]).join(', '); }).join(', ');
      }
      if (!response.ok) throw new Error(String(response.status));

      if (document.querySelector('.template-cart')) {
        var cartForm = document.querySelector('cart-form');
        if (cartForm && cartForm.enableAjaxUpdate) cartForm.refresh();
      } else if (theme.settings.afterAddToCart === 'page') {
        window.location.href = theme.routes.cart;
      } else {
        if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length && sequence >= renderedSequence) {
          renderedSequence = sequence;
          sectionsToUpdate.forEach(function update(el) {
            var html = data.sections[el.dataset.sectionId];
            if (el.matches('.js-cart-drawer')) {
              // One inert parse of the drawer's reply, shared by everything that has to be settled
              // before the merge replaces the drawer's DOM. A <template> is used because its
              // contents are parsed without loading any of their images.
              var reply = document.createElement('template');
              reply.innerHTML = html;
              adoptReplyKey(instant, reply.content);
              warmReplyLineImage(instant, reply.content);
              drawerSuggestions.handOver(reply.content);
              // The merge protects every provisional line but this add's own, which has just been
              // given the key that lets the merge rewrite it where it stands.
              mergingLine = instant ? instant.line : null;
            }
            el.updateFromCartChange(html);
            mergingLine = null;
          });
        } else {
          // The theme's own fallback for an unexpected null section return. It re-renders
          // asynchronously, so the focus repair and on:cart:add below wait for on:cart:after-merge,
          // and the provisional line is settled against the list that merge draws rather than
          // against a reply there is nothing to read.
          mergeIsAsync = true;
          beginPendingRefresh();
          dispatchOn().dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        }

        if (theme.settings.afterAddToCart === 'notification') {
          var template = document.getElementById('AddedNotification');
          if (template) {
            var notification = template.content.firstElementChild.cloneNode(true);
            notification.dataset.productTitle = data.product_title;
            (document.querySelector('.pageheader--sticky') || document.body).appendChild(notification);
          }
        }
        // A drawer opened on the tap needs nothing here: the merge above has just refreshed it,
        // and opening it again would re-dispatch on:cart-drawer:before-open, which
        // cart-drawer.js answers by re-initialising every <cc-cart-cross-sell>. A drawer whose
        // opening was deferred (the bag was empty) opens now, complete.
        if (instant && instant.deferOpen && !instant.drawer.hasAttribute('open')) {
          instant.drawer.open(instant.opener || bar);
          instant.opened = true;
        }
      }

      // The focus repair sits outside the after-add if-chain on purpose: the loss is caused by the
      // re-render, not by the merchant's after-add setting. When a closed drawer was opened above,
      // SideDrawer.open() has already put focus inside it and this is a no-op.
      var finishAdd = function () {
        if (mergeIsAsync) adoptMergedLine(instant);
        settleInstantAdd(instant);
        if (addedFromDrawer) {
          var openDrawer = document.querySelector('.js-cart-drawer');
          if (openDrawer) restoreDrawerFocus(openDrawer);
        }
        dispatchOn().dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: data.variant_id }
        }));
      };
      if (mergeIsAsync) afterNextMerge(finishAdd);
      else finishAdd();

      if (open && open.sheet === sheet) closeSheet();
      else if (sheet.classList.contains('is-open')) hideSheet(state);
    } catch (error) {
      console.warn(error); // eslint-disable-line no-console
      abandonInstantAdd(instant);
      // Shopify's own message (a stock limit, for one) when it sent one; the locale string when the
      // request itself failed.
      var message = responseText || block.dataset.quickAddError || '';
      // The message needs a tracked, open picker: a direct add has none on show, and a card
      // released while another card was opened is no longer tracked. Re-open this card's picker -
      // which closes any other - and show the message there; its buttons double as the retry.
      if (!(open && open.sheet === sheet)) {
        if (open) closeSheet(true);
        openSheet(block, bar);
      }
      setError(sheet, message);
      dispatchOn().dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: message }
      }));
      // The cart may have changed despite the error, as the theme's own <product-form> assumes.
      beginPendingRefresh();
      dispatchOn().dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    } finally {
      if (instant) endPendingAdd(instant.drawer);
      block.quickAddBusy = false;
      sheet.classList.remove('is-adding');
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }

  // Marks the tapped card swatch, on every card and at every width. The theme's own
  // ProductBlock.onSelectSwatch still runs after this and swaps the photo; this only records the
  // choice. Registered before the bar handler, so an open picker re-renders in the same tap.
  document.addEventListener('click', function onSwatchTap(evt) {
    var item = evt.target.closest ? evt.target.closest('.product-block-options__item[data-option-item]') : null;
    if (!item) return;
    var row = item.closest('.product-block-options');
    if (!row) return;

    var previous = row.querySelectorAll('.product-block-options__item--selected');
    for (var i = 0; i < previous.length; i += 1) {
      if (previous[i] !== item) previous[i].classList.remove('product-block-options__item--selected');
    }
    item.classList.add('product-block-options__item--selected');

    if (open && open.block === item.closest('.product-block') && !open.block.quickAddBusy) {
      setError(open.sheet, '');
      renderOptions(open.block, open.data, open.sheet);
    }
  }, true);

  // Capture phase on the document, so this runs before the theme's delegated .quickbuy-toggle
  // handler (a bubble-phase document listener). That handler only acts at 768px and up, and below
  // that it lets the link navigate - which preventDefault() here stops - so the event is left to
  // propagate: stopping it would also swallow the theme's other document-level click listeners.
  document.addEventListener('click', function onTap(evt) {
    var bar = evt.target.closest ? evt.target.closest('.quick-add-bar') : null;

    // The one exception. An "always" card has no .quickbuy-container, so at 768px and up the
    // theme's handler would preventDefault() the link and then throw on the missing container. With
    // the theme's quick buy style set to "whole" the image and title links carry .quickbuy-toggle
    // too, so propagation is cut for any toggle on such a card - only propagation: what the tap
    // then does is still decided below, and those links still navigate.
    var toggle = evt.target.closest ? evt.target.closest('.quickbuy-toggle') : null;
    if (toggle && toggle.closest(SHEET_ALWAYS) && window.innerWidth >= THEME_QUICKBUY_MIN_WIDTH) {
      evt.stopPropagation();
    }

    if (bar) {
      // The theme closes its desktop panel by dispatching a synthetic CustomEvent 'click' on the
      // toggle; that is never a shopper's tap, so it must not open the picker.
      if (typeof MouseEvent !== 'undefined' && !(evt instanceof MouseEvent)) return;
      var block = bar.closest('.product-block');
      if (!usesSheet(block)) {
        // At a desktop width an "always" picker can be open while the shopper taps a normal card's
        // bar (the theme's panel path); close it first so the two never show at once.
        if (open) closeSheet();
        return;
      }
      var data = block ? readData(block) : null;
      if (!data || !quickAddable(data)) return; // plain link to the product page

      evt.preventDefault();
      if (block.quickAddBusy) return; // a repeat tap on an adding card is ignored until it settles
      if (open && open.block === block) {
        closeSheet();
        return;
      }
      // Nothing to choose: add straight away, no picker. A sold-out one opens the picker instead,
      // whose single inert button says so.
      var direct = directVariant(block, data);
      if (direct && direct[2]) {
        releaseOpenFor(block); // another card's idle picker must not outlive this add
        if (data.swatch > -1) markSwatch(block, data, currentSwatch(block, data));
        addVariant({ block: block, bar: bar, sheet: ensureSheet(block), data: data }, bar, direct[0]);
        return;
      }
      openSheet(block, bar);
      return;
    }

    if (!open) return;
    if (open.sheet.contains(evt.target)) return; // the picker handles its own buttons
    if (open.block.contains(evt.target)) {
      // A swatch tap on the open card re-renders it (handled above). Any other tap on the open card
      // - its photo, its title - reads as "put the sizes away", not as "open the product".
      if (evt.target.closest('.product-block-options__item')) return;
      evt.preventDefault();
      closeSheet();
      return;
    }
    // A tap anywhere else closes the picker and goes on to do what it would have done.
    closeSheet();
  }, true);
}());



/**
 * The bag drawer's suggestion row, kept in step with the bag rather than rebuilt.
 *
 * The theme's own <cc-cart-cross-sell> answers every drawer open and every cart change by
 * refetching the recommendations section and rewriting the whole carousel, which empties the row
 * and reloads photos the shopper is already looking at. Once the row has cards it is patched
 * here instead: what the bag now holds fades out, and a replacement is fetched in the background
 * and appended. An empty row is left to the theme, so the first fill is unchanged.
 *
 * The section's cross-sells region is cached on empty-or-not for the same reason (see
 * sections/cart-drawer.liquid), so a bag whose membership changed no longer replaces the row's
 * markup underneath this.
 */
const drawerSuggestions = (function suggestionRow() {
  /** How long a card takes to fade before the row closes up over it. Kept in step with the
   *  .slider__item--leaving transition in assets/custom.css. */
  const CARD_FADE_MS = 300;

  /** The recommendations URL from the reply to a quick add, whose fingerprint is already current.
   *  Null - no reply, no row in it, or a row carrying no URL - means the URL on the element is of
   *  unknown age and has to be rebuilt before it is used. */
  let handedFrom = null;

  /** Bumped by every sync. A suggestions fetch records the generation it started in and drops its
   *  reply if a later cart change has taken over, because the bag it was asked about has moved. */
  let generation = 0;

  /** The product a link points at, by handle: the one name a bag line and a card share. */
  function handleFromUrl(url) {
    if (!url) return '';
    const marker = '/products/';
    const path = url.split('?')[0].split('#')[0];
    const at = path.indexOf(marker);
    if (at < 0) return '';
    return path.slice(at + marker.length).split('/')[0];
  }

  /** The row's cards, less any the theme is currently fading out. */
  function liveItems(grid) {
    return Array.from(grid.querySelectorAll('.slider__item:not(.merge-remove-item)'));
  }

  function cardHandle(item) {
    const link = item.querySelector('a.product-link');
    return handleFromUrl(link ? link.getAttribute('href') : '');
  }

  /**
   * What the bag holds, read from the drawer's own lines: no request, and already merged.
   *
   * Only lines Shopify has confirmed are counted. A line still waiting on a reply carries a key
   * this file made up rather than one the section rendered, so the `item-key:` test leaves it out;
   * counting it would take that product's card off the row before the add was agreed, and a
   * failure would then have nowhere to put its message - the card holding the picker would be
   * gone.
   * @param {Element} drawer
   * @returns {Array<string>}
   */
  function bagHandles(drawer) {
    const confirmed = drawer.querySelectorAll('[data-merge-list-item^="item-key:"]:not(.merge-remove-item) .cart-item__image-link');
    return Array.from(confirmed)
      .map((link) => handleFromUrl(link.getAttribute('href')))
      .filter((handle) => handle !== '');
  }

  function refreshCarousels(host) {
    host.querySelectorAll('carousel-slider').forEach((slider) => {
      if (typeof slider.refresh === 'function') slider.refresh();
    });
  }

  /**
   * Puts focus back inside the drawer when it left with something that has just been removed. An
   * aria-modal drawer whose focus has fallen to <body> traps nothing: the next Tab walks into the
   * page behind it. The theme's own trapFocus is reused because it re-scans the drawer.
   * @param {Element} drawer
   */
  function keepFocusInDrawer(drawer) {
    if (!drawer || !drawer.hasAttribute('open')) return;
    const active = document.activeElement;
    if (active && active !== document.body && drawer.contains(active)) return;
    focusDrawerWithoutScrolling(drawer);
  }

  /**
   * Takes a card out of the row: it fades where it stands, then goes, and the row closes up.
   *
   * The theme's own hideAndRemove animates height, which is the wrong axis here - in a horizontal
   * carousel it would leave the card's full width as a blank gap for three quarters of a second.
   * Closing the width instead is not open to us either: the row is a grid whose column width comes
   * from grid-auto-columns, not from the card, so the space is the track's and only goes when the
   * card leaves the grid. So the fade is ours and the space closes as the card is removed.
   *
   * The theme's own merge-remove-item class is the marker, so everything that already skips a card
   * on its way out - liveItems, the cap's trim - keeps skipping this one.
   * @param {Element} item - The .slider__item to remove.
   * @param {Element} host - The <cc-cart-cross-sell> the row belongs to.
   */
  function removeRowCard(item, host) {
    if (item.classList.contains('merge-remove-item')) return;
    const drawer = host.closest('.js-cart-drawer');
    // The row is as tall as its tallest card; if that is the card leaving, the row shrinks and a
    // drawer scrolled to its end hops up by the difference. The row's block keeps its height
    // instead (the custom elements around the row are inline boxes, which a min-height ignores).
    const block = host.closest('.cart-item-upsells') || host;
    block.style.minHeight = `${block.getBoundingClientRect().height}px`;
    item.classList.add('merge-remove-item');
    // One synchronous style read, so the fade runs from the card's painted state.
    void item.offsetWidth; // eslint-disable-line no-void
    item.classList.add('slider__item--leaving');

    window.setTimeout(() => {
      item.remove();
      refreshCarousels(host);
      // Done here rather than after the merge: the card is still in the drawer at that point, so
      // focus inside it has not been lost yet - it is lost now, as the card goes.
      keepFocusInDrawer(drawer);
    }, CARD_FADE_MS);
  }

  /**
   * Called by the quick add path with the reply's own copy of the drawer, before the merge
   * replaces the live one. All that is taken is the recommendations URL, whose cache fingerprint
   * the reply has already rebuilt for the new bag.
   * @param {DocumentFragment} reply - The parsed drawer section from the add's reply.
   */
  function handOver(reply) {
    const source = reply.querySelector('cc-cart-cross-sell');
    handedFrom = (source && source.dataset.from) || null;
    if (!handedFrom) return;

    // Put it on the row now, before the merge. A row that has not filled yet is still the theme's
    // to fill, and cart-drawer.js's own after-merge listener reaches that fetch before sync()
    // does; left alone it would ask for the bag as it stood before this add, and offer back the
    // very thing that was just added.
    const host = document.querySelector('.js-cart-drawer cc-cart-cross-sell');
    if (host) host.dataset.from = handedFrom;
  }

  /**
   * The recommendations URL with its fingerprint brought up to date. Only reached after a cart
   * change this file did not make - the theme's own product form - and only when the row is short
   * of cards, so it never sits on the add path.
   * @param {string} from - The URL as the row currently carries it.
   * @returns {Promise<string>}
   */
  function freshenedFrom(from) {
    return fetch(`${theme.routes.cart}.js`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        const ids = (cart.items || []).map((item) => item.product_id);
        const url = new URL(from, window.location.href);
        url.searchParams.set('product_id', ids.length ? ids[0] : '');
        url.searchParams.set('cart', ids.join('-'));
        return url.pathname + url.search;
      });
  }

  /**
   * Fills the row back up to the merchant's cap without clearing it: the cards already on screen
   * stay exactly where they are, and only what is missing is fetched and appended. Nothing
   * already in the bag and nothing already on the row can be appended.
   * @param {Element} host - The <cc-cart-cross-sell> element.
   * @param {Element} grid - Its .product-grid.
   * @param {boolean} fingerprintIsCurrent - Whether host's data-from already follows the new bag.
   * @param {number} startedIn - The sync generation this fetch belongs to.
   */
  function topUp(host, grid, fingerprintIsCurrent, startedIn) {
    const cap = host.closest('cc-drawer-upsell-cap');
    const limit = cap ? parseInt(cap.dataset.limit, 10) : 0;
    const from = host.dataset.from || '';
    if (!limit || !from || liveItems(grid).length >= limit) return;

    (fingerprintIsCurrent ? Promise.resolve(from) : freshenedFrom(from))
      .then((resolved) => {
        if (startedIn !== generation) return null;
        host.dataset.from = resolved;
        return fetch(resolved);
      })
      .then((response) => {
        if (!response) return null;
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (html === null || startedIn !== generation || !host.isConnected) return;

        // The bag is read again rather than trusted from before the fetch: it is what decides
        // whether a card may be offered, and the request took time.
        const drawer = host.closest('.js-cart-drawer');
        const inBag = drawer ? bagHandles(drawer) : [];

        // Parsed in a <template> so none of the offered cards' photos are fetched; only the ones
        // that are actually appended cost anything.
        const parsed = document.createElement('template');
        parsed.innerHTML = html;
        const offered = parsed.content.querySelectorAll('.product-grid > .slider__item');
        const shown = liveItems(grid).map(cardHandle);
        let added = false;

        for (let i = 0; i < offered.length && liveItems(grid).length < limit; i += 1) {
          const handle = cardHandle(offered[i]);
          if (handle && shown.indexOf(handle) === -1 && inBag.indexOf(handle) === -1) {
            shown.push(handle);
            offered[i].classList.add('is-entering');
            grid.appendChild(offered[i]);
            // Only the new card: over the whole row this would re-assign the src of every photo
            // already on screen, asking for each of them a second time.
            theme.manuallyLoadImages(offered[i]);
            // One synchronous style read, so the entering state is laid out before it is taken
            // away; without it the browser coalesces both and the card appears rather than fades.
            void offered[i].offsetWidth; // eslint-disable-line no-void
            offered[i].classList.remove('is-entering');
            added = true;
          }
        }

        if (!added) return;
        refreshCarousels(host);
      })
      .catch((error) => {
        console.warn(error); // eslint-disable-line no-console
      });
  }

  /** Brings the row into line with the bag the merge has just drawn. */
  function sync() {
    const handed = handedFrom;
    handedFrom = null;
    generation += 1;
    const startedIn = generation;

    const drawer = document.querySelector('.js-cart-drawer');
    if (!drawer) return;
    const host = drawer.querySelector('cc-cart-cross-sell');
    if (!host) return;
    const grid = host.querySelector('.product-grid');
    if (!grid || liveItems(grid).length === 0) return; // an empty row is the theme's to fill

    if (handed) host.dataset.from = handed;

    // What the bag now holds has no place in a row of things to add to it.
    const inBag = bagHandles(drawer);
    let removed = false;
    liveItems(grid).forEach((item) => {
      if (inBag.indexOf(cardHandle(item)) === -1) return;
      removeRowCard(item, host);
      removed = true;
    });
    if (removed) refreshCarousels(host);

    topUp(host, grid, !!handed, startedIn);
  }

  /**
   * Fills an empty row from the recommendations endpoint, as the theme's own element does, with
   * one difference: a reply is used only while the URL it was asked for is still the URL the row
   * carries.
   *
   * The theme cannot make that check, and its fetch is started by the drawer's before-open
   * handler - which, now that a quick add opens the drawer on the tap, runs with the bag as it
   * stood before the add. On a bag that was already full when the page loaded, that older reply
   * can land after the newer one and put back the very product the shopper has just added.
   * @param {Element} host - The <cc-cart-cross-sell> element.
   */
  function fillRow(host) {
    const asked = host.dataset.from;
    const grid = host.querySelector('.product-grid');
    if (!asked || !grid) return;

    fetch(asked)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        if (host.dataset.from !== asked) return; // the bag moved on while this was in flight
        const parsed = document.createElement('template');
        parsed.innerHTML = html;
        const offered = parsed.content.querySelector('.product-grid');
        // Hiding an empty row stays the theme's own behaviour, and so does never showing it again.
        if (!offered) {
          host.classList.add('hidden');
          return;
        }
        grid.innerHTML = offered.innerHTML;
        refreshCarousels(host);
      })
      .catch((error) => {
        console.warn(error); // eslint-disable-line no-console
      });
  }

  const CrossSell = window.customElements.get('cc-cart-cross-sell');
  if (CrossSell) {
    const themeInit = CrossSell.prototype.init;
    CrossSell.prototype.init = function init() {
      // Outside the drawer nothing here applies, so the theme keeps its own element.
      if (!this.closest('.js-cart-drawer')) {
        themeInit.call(this);
        return;
      }
      const grid = this.querySelector('.product-grid');
      if (grid && liveItems(grid).length > 0) return; // a filled row is patched, never rebuilt
      fillRow(this);
    };
  }

  document.addEventListener('on:cart:after-merge', sync);

  return { handOver };
}());



/**
 * Bag drawer: the suggestion row's cap and photo warming, and the free-delivery bars; and the
 * same photo warming for the cards on screen of the grids whose photos swipe (collection, search,
 * the product page's suggestions).
 *
 * <cc-drawer-upsell-cap> trims the suggestion carousel to the merchant's "Maximum products
 * shown" setting. The drawer asks the recommendations endpoint for that number plus a buffer,
 * because sections/mini-related-products.liquid drops what is already in the bag and what is
 * sold out; the trim back to the cap has to happen here, since a section fetched by URL cannot
 * read the drawer section's settings.
 *
 * The free-delivery bars themselves are rendered and refreshed in Liquid, so nothing here
 * fetches the cart. What Liquid cannot do is move things: the fill's width and the money emoji's
 * flight along it are set from the progress each bar carries as a data attribute. The drawer's
 * bar is refreshed by the theme's section merge; the product page's own section is not
 * re-rendered by a cart change, so its bar is handed the drawer's progress and wording instead of
 * working them out again.
 */
(function drawerRow() {
  /** How long the emoji takes to fly to its new place; matches the fly-wave keyframes. */
  const FLY_MS = 2000;
  /** How long the celebration pop is left on once the flight lands. */
  const CELEBRATE_MS = 900;
  /** Where the emoji parks with an empty bag, and how far its 24px glyph is pulled back from
   *  the fill's leading edge so it sits on that edge rather than past it. */
  const REST_LEFT_PX = -6;
  const TIP_INSET_PX = 12;

  /** How long the browser may put the photo warming off before it is run anyway. */
  const PRELOAD_IDLE_TIMEOUT_MS = 2000;
  /** The wait used instead where the browser has no idle callback. */
  const PRELOAD_FALLBACK_MS = 500;

  let preloadScheduled = false;
  /** The row currently being watched for coming into view, and whether it ever has. */
  let watchedRow = null;
  let rowHasBeenSeen = false;
  let rowObserver = null;

  /**
   * Asks the browser for the same candidate it will pick when the photo is swapped, by giving a
   * detached image the card image's own sizes and srcset. Nothing here decides a width: what the
   * card asks for is the section's business.
   * @param {HTMLImageElement} image
   */
  function warmImage(image) {
    const srcset = image.getAttribute('srcset') || image.dataset.manualSrcset || '';
    const src = image.getAttribute('src') || image.dataset.manualSrc || '';
    if (!srcset && !src) return;

    const probe = new Image();
    // Behind everything the page is still fetching for itself.
    probe.fetchPriority = 'low';
    const sizes = image.getAttribute('sizes');
    // sizes before srcset: the candidate is chosen the moment srcset is set.
    if (sizes) probe.sizes = sizes;
    if (srcset) probe.srcset = srcset;
    // With width candidates in the srcset the browser never picks src, so it is only set when
    // there is no srcset to choose from.
    else probe.src = src;
  }

  /**
   * The colour photos a card is not showing, warmed once per card, at most `limit` of them; used by
   * the drawer's row (no limit) and the grids (the remaining page budget). Returns how many were warmed.
   */
  function warmCardColourPhotos(card, limit) {
    if (card.colourPhotosWarmed || !card.isConnected) return 0;
    const active = card.querySelector('.product-block__image--active');
    const activeId = active ? active.dataset.mediaId : '';
    const max = limit === undefined ? Infinity : limit;
    let eligible = 0;
    let warmed = 0;
    card.querySelectorAll('.product-block-options__item[data-media]').forEach((dot) => {
      if (!dot.dataset.media || dot.dataset.media === activeId) return;
      // Dots past the card's swatch limit are hidden at this width (a phone cannot reach them;
      // a desktop reveals them on hover), so they are not warmed here.
      if (dot.offsetParent === null) return;
      const image = card.querySelector(`.product-block__image[data-media-id="${dot.dataset.media}"] img`);
      if (!image) return;
      eligible += 1;
      if (warmed >= max) return;
      warmImage(image);
      warmed += 1;
    });
    // Only a fully served card is done; one cut short by the budget is left for a later pass.
    if (warmed === eligible) card.colourPhotosWarmed = true;
    return warmed;
  }

  /**
   * Warms the colour photos the suggestion cards are not showing, so a colour dot swaps the photo
   * without a round trip. Only runs while the drawer is open, only over the cards inside the
   * merchant's cap, and only once per card.
   */
  function warmRowColourPhotos() {
    const drawer = document.querySelector('.js-cart-drawer');
    if (!drawer || !drawer.hasAttribute('open')) return;
    const host = drawer.querySelector('cc-cart-cross-sell');
    if (!host) return;

    const cap = host.closest('cc-drawer-upsell-cap');
    const limit = cap ? parseInt(cap.dataset.limit, 10) : 0;
    let cards = Array.from(host.querySelectorAll('.product-block'));
    if (limit > 0) cards = cards.slice(0, limit);

    cards.forEach((card) => warmCardColourPhotos(card));
  }

  /** Queues the warming for the browser's idle time, at most one run at a time. */
  function scheduleColourWarming() {
    if (preloadScheduled) return;
    preloadScheduled = true;
    const run = () => {
      preloadScheduled = false;
      warmRowColourPhotos();
    };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: PRELOAD_IDLE_TIMEOUT_MS });
    else window.setTimeout(run, PRELOAD_FALLBACK_MS);
  }

  /**
   * Waits for the suggestion row to be on screen before any of it is warmed. The row sits below
   * the bag lines and a shopper with a full bag may never scroll to it, so photos for it are not
   * worth a request until it has been seen. The watch is one-shot per row: once it has been on
   * screen, later changes to it - cards appended as the bag changes - are warmed straight away.
   */
  function requestColourWarming() {
    const drawer = document.querySelector('.js-cart-drawer');
    if (!drawer || !drawer.hasAttribute('open')) return;
    const row = drawer.querySelector('cc-cart-cross-sell');
    if (!row) return;

    if (row !== watchedRow) {
      watchedRow = row;
      rowHasBeenSeen = false;
      if (rowObserver) {
        rowObserver.disconnect();
        rowObserver = null;
      }
    }
    if (rowHasBeenSeen) {
      scheduleColourWarming();
      return;
    }
    if (rowObserver) return; // already waiting for this row to come into view

    // The drawer's content is the thing that scrolls, so it is what the row is on screen within.
    const root = drawer.querySelector('.cart-drawer__content');
    if (!window.IntersectionObserver || !root) {
      rowHasBeenSeen = true;
      scheduleColourWarming();
      return;
    }

    rowObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      rowObserver.disconnect();
      rowObserver = null;
      rowHasBeenSeen = true;
      scheduleColourWarming();
    }, { root });
    rowObserver.observe(row);
  }

  class DrawerUpsellCap extends HTMLElement {
    connectedCallback() {
      this.limit = parseInt(this.dataset.limit, 10);
      this.refresh();

      // The row's contents change under this element - the theme's first fill, and the cards
      // appended as the bag changes - so the cap has to be re-applied whenever the subtree
      // changes, not just once.
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    refresh() {
      this.trim();
      this.unhideWhenPopulated();
      requestColourWarming();
    }

    /** Removes any card past the cap, then refreshes the carousel so its nav matches. */
    trim() {
      if (this.trimming || !this.limit || this.limit < 1) return;

      const grid = this.querySelector('.product-grid');
      if (!grid) return;

      // A card on its way out has already left the row as far as the cap is concerned; counting
      // it would push its replacement back off again.
      const items = Array.from(grid.children).filter((item) => !item.classList.contains('merge-remove-item')
        && !item.classList.contains('merge-remove-wrapper'));
      if (items.length <= this.limit) return;

      this.trimming = true;
      items.slice(this.limit).forEach((item) => item.remove());
      this.querySelectorAll('carousel-slider').forEach((slider) => {
        if (typeof slider.refresh === 'function') slider.refresh();
      });
      this.trimming = false;
    }

    /**
     * Shows the row once it has cards. An automatic row is rendered hidden (sections/cart-drawer.liquid)
     * and the theme's CCCartCrossSell also hides it when a fetch comes back empty, without ever
     * showing it again; this only ever removes that class, so hiding stays the theme's decision,
     * and a failed fetch leaves the row hidden. The carousel is refreshed after showing, because
     * the theme measured it while it was still hidden.
     */
    unhideWhenPopulated() {
      const host = this.querySelector('cc-cart-cross-sell');
      if (!host || !host.classList.contains('hidden')) return;

      const grid = host.querySelector('.product-grid');
      if (!grid || grid.children.length === 0) return;
      host.classList.remove('hidden');
      host.querySelectorAll('carousel-slider').forEach((slider) => {
        if (typeof slider.refresh === 'function') slider.refresh();
      });
    }
  }

  window.customElements.define('cc-drawer-upsell-cap', DrawerUpsellCap);

  document.addEventListener('on:cart-drawer:after-open', requestColourWarming);

  /* The two free-delivery bars this file drives. Each names the element carrying the progress,
     the track the fill and the emoji move along, and the animation classes its own stylesheet
     defines; everything else about them is identical. */
  const DRAWER_BAR = {
    progress: '#vr-drawer-ship-container',
    bar: '#vr-drawer-ship-container',
    fill: '#vr-drawer-ship-fill',
    emoji: '#vr-drawer-ship-emoji',
    text: '#vr-drawer-ship-text',
    flying: 'vr-emoji-flying',
    celebrate: 'vr-emoji-celebrate'
  };
  const PRODUCT_BAR = {
    progress: '#free-shipping-progress',
    bar: '#free-shipping-progress .progress-container',
    fill: '#progress-bar',
    emoji: '#flying-emoji',
    text: '#free-shipping-text',
    flying: 'emoji-flying',
    celebrate: 'emoji-celebrate'
  };

  // Everything below serves a bar, so it stays uninstalled on a page that renders neither.
  if (!document.querySelector(DRAWER_BAR.progress) && !document.querySelector(PRODUCT_BAR.progress)) return;

  /**
   * One bar's driver. Each bar keeps its own timers and its own memory of what it last drew,
   * because the two are laid out differently and move independently.
   * @param {object} spec - One of the bar descriptions above.
   * @returns {Function} Puts the fill and the emoji where that bar's progress says they belong.
   */
  function barDriver(spec) {
    let flyTimer = null;
    let celebrateTimer = null;
    let shownProgress = null;
    let shownWidth = null;

    /**
     * The flight is only run when the progress has actually moved, so reopening the drawer does
     * not replay it - and a redraw with the same progress leaves a flight that is still under way
     * untouched: the drawer opens a moment after the cart merge, and both fire this. A parked
     * emoji is only re-placed when the bar has changed width since it was last placed.
     */
    return function draw() {
      const source = document.querySelector(spec.progress);
      const bar = document.querySelector(spec.bar);
      const fill = document.querySelector(spec.fill);
      const emoji = document.querySelector(spec.emoji);
      if (!source || !bar || !fill || !emoji) return;

      const progress = Math.min(Math.max(parseFloat(source.dataset.freeShippingProgress) || 0, 0), 1);
      const moved = progress !== shownProgress;

      if (!moved) {
        if (flyTimer === null && shownWidth !== bar.offsetWidth) {
          shownWidth = bar.offsetWidth;
          emoji.style.transition = '';
          emoji.style.left = progress === 0 ? `${REST_LEFT_PX}px` : `${Math.max(0, (bar.offsetWidth * progress) - TIP_INSET_PX)}px`;
        }
        return;
      }
      shownProgress = progress;
      shownWidth = bar.offsetWidth;

      window.clearTimeout(flyTimer);
      window.clearTimeout(celebrateTimer);
      flyTimer = null;
      emoji.classList.remove(spec.flying, spec.celebrate);
      fill.style.width = `${progress * 100}%`;

      if (progress === 0) {
        emoji.style.transition = '';
        emoji.style.left = `${REST_LEFT_PX}px`;
        return;
      }

      emoji.style.transition = `left ${FLY_MS}ms ease-in-out`;
      emoji.classList.add(spec.flying);
      flyTimer = window.setTimeout(() => {
        flyTimer = null;
        emoji.classList.remove(spec.flying);
        if (progress < 1) return;
        emoji.classList.add(spec.celebrate);
        celebrateTimer = window.setTimeout(() => {
          emoji.classList.remove(spec.celebrate);
        }, CELEBRATE_MS);
      }, FLY_MS);

      emoji.style.left = `${Math.max(0, (bar.offsetWidth * progress) - TIP_INSET_PX)}px`;
    };
  }

  const drawDrawerBar = barDriver(DRAWER_BAR);
  const drawProductBar = barDriver(PRODUCT_BAR);

  /**
   * Gives the product page's bar the progress and the wording the merge has just put on the
   * drawer's, so the two always read the same. Both are rendered from the same snippet and the
   * same language keys, so this is the section's own markup being moved, not new text.
   */
  function copyDrawerBarToProductBar() {
    const source = document.querySelector(DRAWER_BAR.progress);
    const target = document.querySelector(PRODUCT_BAR.progress);
    if (!source || !target) return;

    target.dataset.freeShippingProgress = source.dataset.freeShippingProgress || '0';
    const sourceText = document.querySelector(DRAWER_BAR.text);
    const targetText = document.querySelector(PRODUCT_BAR.text);
    if (sourceText && targetText) targetText.innerHTML = sourceText.innerHTML;
  }

  function drawShippingBars() {
    copyDrawerBarToProductBar();
    drawDrawerBar();
    drawProductBar();
  }

  /* Where %AMOUNT% sits in the wordings the section hands over, for the drawerless path below. */
  const AMOUNT_TOKEN = '%AMOUNT%';

  let cartReadInFlight = false;
  let cartReadAgain = false;

  /**
   * The shop's money format. The bar carries it when the section can pass it on; otherwise the
   * theme's own exposed format is used - the same one assets/shipping-calculator.js writes rates
   * with, and the only one this theme puts within reach of JavaScript.
   * @param {Element} host
   * @returns {string}
   */
  function moneyFormat(host) {
    return (host && host.dataset.moneyFormat)
      || (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat)
      || '{{amount}}';
  }

  /**
   * Writes an amount in the shop's minor units into a money format string. Shopify's own token
   * names say how the number is written: whether it carries decimals, and whether the decimal mark
   * is a point or a comma.
   * @param {number} minorUnits
   * @param {string} format
   * @returns {string}
   */
  function formatMoney(minorUnits, format) {
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function write(match, token) {
      const comma = token.indexOf('comma_separator') > -1;
      const amount = (minorUnits / 100).toFixed(token.indexOf('no_decimals') > -1 ? 0 : 2);
      const parts = amount.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
      return parts.length > 1 ? `${whole}${comma ? ',' : '.'}${parts[1]}` : whole;
    });
  }

  /**
   * Brings the product page's bar up to date from the cart itself.
   *
   * Only for the one store setup where nothing else on the page carries the cart's new state after
   * an add: the bag is a page rather than a drawer, and adding shows a notification, so there is no
   * drawer section for the theme to re-render and no bar to copy from. The threshold and both
   * wordings ride on the bar as data attributes, so this reads the cart once - never on a hot path
   * - and writes the same sentence the section would have written.
   */
  function refreshProductBarFromCart() {
    const host = document.querySelector(PRODUCT_BAR.progress);
    if (!host) return;
    const threshold = parseInt(host.dataset.freeShippingAmount, 10);
    if (!threshold || threshold < 1) return;

    // One read at a time: an add dispatches more than one cart event, and they all mean the same
    // question. A request that arrives while one is in flight is answered by re-asking after it.
    if (cartReadInFlight) {
      cartReadAgain = true;
      return;
    }
    cartReadInFlight = true;

    fetch(`${theme.routes.cart}.js`)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        const total = cart.total_price || 0;
        const progress = Math.min(Math.max(total / threshold, 0), 1);
        host.dataset.freeShippingProgress = progress;

        const text = document.querySelector(PRODUCT_BAR.text);
        if (text) {
          text.innerHTML = progress < 1
            ? (host.dataset.deficitHtml || '').replace(AMOUNT_TOKEN, formatMoney(threshold - total, moneyFormat(host)))
            : (host.dataset.metHtml || '');
        }
        drawProductBar();
      })
      .catch((error) => {
        console.warn(error); // eslint-disable-line no-console
      })
      .then(() => {
        cartReadInFlight = false;
        if (!cartReadAgain) return;
        cartReadAgain = false;
        refreshProductBarFromCart();
      });
  }

  drawShippingBars();
  // after-merge is the theme's "the cart drawer has been re-rendered" event, so the new progress
  // is already on the element by the time this runs; after-open covers a drawer opened without any
  // cart change, where the bar simply has to be in the right place.
  document.addEventListener('on:cart:after-merge', drawShippingBars);
  document.addEventListener('on:cart-drawer:after-open', drawShippingBars);

  // The drawer's bar is the source wherever there is one: the merge refreshes it and the product
  // bar is copied from it. Only a page without one has to ask the cart.
  if (!document.querySelector(DRAWER_BAR.progress)) {
    document.addEventListener('on:cart:add', refreshProductBarFromCart);
    document.addEventListener('on:cart:change', refreshProductBarFromCart);
  }
  /*
   * Grid cards. A card on a collection or search grid (and the product page's suggestion grid: the
   * grids whose cards swipe through their photos) carries one photo per colour, and the theme
   * loads only the one on view; a tap on a colour dot then waits for that photo's download. The
   * drawer's row has warmed its other colours since round 19; those grids get the same here: once
   * the page has loaded and a card has been on screen, its other colours' photos are fetched in
   * the browser's idle time, one card per idle slice, at low priority, once per card, up to a
   * budget of photos per page; when the budget is spent the watching stops. Carousels, the
   * search-as-you-type dropdown and the drawer are not watched (the drawer warms its own row).
   * Chrome's data-saver mode or a 2G-class connection turns it off; Safari has no such signal.
   * Cards added later (load more, filtering, sorting) are picked up while the budget lasts.
   */
  /** The most photos the grid warming fetches on one page. */
  const GRID_WARM_PHOTO_BUDGET = 24;
  /** Cards on a grid whose photos swipe, outside the drawer. */
  const GRID_CARD = '.product-block:not(.product-block--static-photos)';

  /** Cards seen on screen and not yet warmed, in the order they were seen. */
  const gridWarmQueue = [];
  let gridWarmScheduled = false;
  let gridWarmedPhotos = 0;
  let gridObserver = null;
  let gridMutations = null;

  /** Once the page's budget is spent nothing else is watched, queued or held. */
  function stopGridWarming() {
    if (gridObserver) gridObserver.disconnect();
    if (gridMutations) gridMutations.disconnect();
    gridObserver = null;
    gridMutations = null;
    gridWarmQueue.length = 0;
  }

  /** Warms one queued card per idle slice, and comes back for the rest while the budget lasts. */
  function scheduleGridWarming() {
    if (gridWarmScheduled || !gridWarmQueue.length || gridWarmedPhotos >= GRID_WARM_PHOTO_BUDGET) return;
    gridWarmScheduled = true;
    const run = () => {
      gridWarmScheduled = false;
      const card = gridWarmQueue.shift();
      if (card) gridWarmedPhotos += warmCardColourPhotos(card, GRID_WARM_PHOTO_BUDGET - gridWarmedPhotos);
      if (gridWarmedPhotos >= GRID_WARM_PHOTO_BUDGET) stopGridWarming();
      else scheduleGridWarming();
    };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: PRELOAD_IDLE_TIMEOUT_MS });
    else window.setTimeout(run, PRELOAD_FALLBACK_MS);
  }

  /** Puts every grid card with colour dots that is not yet watched under the observer. */
  function watchGridCards() {
    document.querySelectorAll(GRID_CARD).forEach((card) => {
      if (card.gridWarmWatched || card.closest('.js-cart-drawer')) return;
      if (!card.querySelector('.product-block-options__item[data-media]')) return;
      card.gridWarmWatched = true;
      gridObserver.observe(card);
    });
  }

  /** Whether a mutation added a grid card (or a subtree holding one), rather than text or other markup. */
  function addsGridCards(record) {
    return Array.prototype.some.call(record.addedNodes, (node) => node.nodeType === 1
      && (node.matches(GRID_CARD) || node.querySelector(GRID_CARD)));
  }

  function startGridWarming() {
    if (!window.IntersectionObserver) return;
    // Chrome's data-saver mode or a 2G-class connection: the photos are not worth their bytes
    // before a tap. Safari exposes neither signal.
    const connection = navigator.connection;
    if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) return;
    gridObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        gridObserver.unobserve(entry.target);
        gridWarmQueue.push(entry.target);
      });
      scheduleGridWarming();
    });
    watchGridCards();
    gridMutations = new MutationObserver((records) => {
      if (records.some(addsGridCards)) watchGridCards();
    });
    gridMutations.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'complete') startGridWarming();
  else window.addEventListener('load', startGridWarming, { once: true });
}());
